import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, DialogContentText,
    List, ListItem, ListItemButton, ListItemIcon, Checkbox, FormControlLabel
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    // Search as SearchIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../Auth';
import { useSnackbar } from '../context/SnackbarContext';
import { formatDate, formatForInput } from '../utils/dateUtils';
import api from '../api/axiosInstance';

export default function Ceremonies() {
    const { user } = useAuth();
    const [ceremonies, setCeremonies] = useState([]);
    const [availableAwards, setAvailableAwards] = useState([]);
    const [awardsModalOpen, setAwardsModalOpen] = useState(false);
    const [delays, setDelays] = useState({ open: 4, cutoff: 2 });
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        name: '',
        ceremony_date: '',
        open_date: '',
        cutoff_date: '',
        award_ids: []
    });
    const [isEditing, setIsEditing] = useState(false);
    const [confirmRecalcOpen, setConfirmRecalcOpen] = useState(false);
    const [pendingDate, setPendingDate] = useState('');
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [selectedCeremony, setSelectedCeremony] = useState(null);
    const showSnackbar = useSnackbar();
    const canEdit = user.role === 'admin' || user.role === 'superuser';
    const navigate = useNavigate();

    // // 1. Fetch System Settings on load
    // useEffect(() => {
    //     const fetchDelays = async () => {
    //         try {
    //             const token = localStorage.getItem('token');
    //             const res = await fetch(`${process.env.REACT_APP_API_URL}/api/settings`, {
    //                 headers: { 'Authorization': `Bearer ${token}` }
    //             });
    //             const data = await res.json();
    //             if (res.ok) {
    //                 setDelays({
    //                     open: parseInt(data.ceremony_open_delay_weeks) || 4,
    //                     cutoff: parseInt(data.ceremony_cutoff_delay_weeks) || 2
    //                 });
    //             }
    //         } catch (err) {
    //             console.error("Failed to load dynamic delays", err);
    //         }
    //     };
    //     fetchDelays();
    //     fetchCeremonies();
    // }, []);

    // // Fetch awards when the component mounts (or when the main modal opens)
    // useEffect(() => {
    //     const fetchAwards = async () => {
    //         try {
    //             const token = localStorage.getItem('token');
    //             const res = await fetch(`${process.env.REACT_APP_API_URL}/api/awards`, {
    //                 headers: { 'Authorization': `Bearer ${token}` }
    //             });
    //             const data = await res.json();

    //             // FLATTEN THE DATA HERE
    //             const flattened = data.map(a => ({
    //                 id: a.id,
    //                 ...a.payload // This pulls 'name', 'category', etc. to the top level
    //             }));

    //             setAvailableAwards(flattened);
    //         } catch (err) {
    //             console.error("Failed to fetch awards", err);
    //         }
    //     };
    //     fetchAwards();
    // }, []);

    // const fetchCeremonies = async () => {
    //     try {
    //         const token = localStorage.getItem('token');
    //         const res = await fetch(`${process.env.REACT_APP_API_URL}/api/ceremonies`, {
    //             headers: {
    //                 'Authorization': `Bearer ${token}`
    //             }
    //         });

    //         const data = await res.json();

    //         // Safety: If data is not an array (likely an error object), set to empty array
    //         if (Array.isArray(data)) {
    //             setCeremonies(data);
    //         } else {
    //             console.error("Server Error:", data.message);
    //             setCeremonies([]);
    //         }
    //     } catch (err) {
    //         console.error("Network Error:", err);
    //         setCeremonies([]);
    //     }
    // };

    // useEffect(() => { fetchCeremonies(); }, []);

    const fetchData = useCallback(async () => {
        try {
            const [settingsRes, awardsRes, ceremoniesRes] = await Promise.all([
                api.get('/settings'),
                api.get('/awards'),
                api.get('/ceremonies')
            ]);

            // 1. Process Settings (Delays)
            const settings = settingsRes.data;
            setDelays({
                open: parseInt(settings.ceremony_open_delay_weeks) || 4,
                cutoff: parseInt(settings.ceremony_cutoff_delay_weeks) || 2
            });

            // 2. Process Awards (Flattening)
            const flattenedAwards = awardsRes.data.map(a => ({
                id: a.id,
                ...a.payload
            }));
            setAvailableAwards(flattenedAwards);

            // 3. Process Ceremonies
            setCeremonies(Array.isArray(ceremoniesRes.data) ? ceremoniesRes.data : []);

        } catch (err) {
            showSnackbar('Failed to synchronize ceremony data', 'error');
            console.error("Sync Error:", err);
        }
    }, [showSnackbar]);

    // Single entry point for loading data
    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleCeremonyDateChange = (e) => {
        const newDate = e.target.value;
        if (!newDate) return;

        // Check if we are actually changing the date to avoid logic firing on every click
        // We compare with the original value if it exists
        const isActuallyNew = newDate !== formatForInput(ceremonies.find(c => c.id === form.id)?.ceremony_date);

        if (isEditing && isActuallyNew && (form.open_date || form.cutoff_date)) {
            setPendingDate(newDate);
            setConfirmRecalcOpen(true);
        } else if (!isEditing) {
            // For new ceremonies, just auto-calculate without asking
            performCalculation(newDate);
        }
    };

    const performCalculation = (baseDate) => {
        const autoOpen = calculateOffsetDate(baseDate, delays.open);
        const autoCutoff = calculateOffsetDate(baseDate, delays.cutoff);

        setForm({
            ...form,
            ceremony_date: baseDate,
            open_date: autoOpen,
            cutoff_date: autoCutoff
        });

        // Only show if we actually have values to calculate
        if (baseDate) {
            showSnackbar(`Dates calculated: -${delays.open} & -${delays.cutoff} weeks.`, 'info');
        }
    };


    const isLocked = (cutoff) => new Date() > new Date(cutoff);

    const calculateOffsetDate = (baseDate, weeksBack) => {
        if (!baseDate) return '';
        const date = new Date(baseDate);
        date.setDate(date.getDate() - (weeksBack * 7));
        return date.toISOString().split('T')[0];
    };

    const handleCreateClick = () => {
        setForm({
            name: '',
            ceremony_date: '',
            open_date: '',
            cutoff_date: '',
            award_ids: []
        });
        setIsEditing(false);
        setOpen(true);
    };

    const resetForm = () => {
        setForm({
            name: '',
            ceremony_date: '',
            open_date: '',
            cutoff_date: '',
            award_ids: []
        });
        setIsEditing(false);
        showSnackbar('Form cleared', 'info');
    };

    const handleEditClick = (ceremony) => {
        setForm({
            id: ceremony.id,
            name: ceremony.name,
            ceremony_date: formatForInput(ceremony.ceremony_date),
            open_date: formatForInput(ceremony.open_date),
            cutoff_date: formatForInput(ceremony.cutoff_date),
            award_ids: ceremony.award_ids || []
        });
        setIsEditing(true);
        setOpen(true);
    };

    // const handleSave = async () => {
    //     const token = localStorage.getItem('token');
    //     const method = isEditing ? 'PUT' : 'POST';
    //     const url = isEditing
    //         ? `${process.env.REACT_APP_API_URL}/api/ceremonies/${form.id}`
    //         : `${process.env.REACT_APP_API_URL}/api/ceremonies`;

    //     try {
    //         const res = await fetch(url, {
    //             method: method,
    //             headers: {
    //                 'Content-Type': 'application/json',
    //                 'Authorization': `Bearer ${token}`
    //             },
    //             body: JSON.stringify({
    //                 ...form,
    //                 is_active: true,
    //                 created_by: user.id
    //             })
    //         });

    //         if (res.ok) {
    //             setOpen(false);
    //             setIsEditing(false);
    //             // Reset form completely including the new award_ids array
    //             setForm({
    //                 name: '',
    //                 ceremony_date: '',
    //                 open_date: '',
    //                 cutoff_date: '',
    //                 award_ids: []
    //             });
    //             fetchCeremonies();
    //             showSnackbar(isEditing ? 'Ceremony updated!' : 'Ceremony created!');
    //         } else {
    //             const errorData = await res.json();
    //             showSnackbar(`Error: ${errorData.message}`, 'error');
    //         }
    //     } catch (err) {
    //         showSnackbar("Network error saving ceremony", "error");
    //     }
    // };

    const handleSave = async () => {
        try {
            const payload = {
                ...form,
                award_ids: form.award_ids || [],
                is_active: true,
                created_by: user.id
            };

            if (isEditing) {
                await api.put(`/ceremonies/${form.id}`, payload);
            } else {
                await api.post('/ceremonies', payload);
            }

            setOpen(false);
            resetForm(); // Use your existing reset function
            fetchData(); // Refresh everything
            showSnackbar(isEditing ? 'Ceremony updated!' : 'Ceremony created!');
        } catch (err) {
            const msg = err.response?.data?.message || "Error saving ceremony";
            showSnackbar(msg, "error");
        }
    };


    const handleDeleteClick = (ceremony) => {
        setSelectedCeremony(ceremony);
        setDeleteConfirmOpen(true);
    };

    // const handleConfirmDelete = async () => {
    //     if (!selectedCeremony) return;

    //     try {
    //         const token = localStorage.getItem('token');
    //         const res = await fetch(`${process.env.REACT_APP_API_URL}/api/ceremonies/${selectedCeremony.id}`, {
    //             method: 'DELETE',
    //             headers: {
    //                 'Authorization': `Bearer ${token}`
    //             }
    //         });

    //         if (res.ok) {
    //             showSnackbar(`${selectedCeremony.name} has been archived.`, 'success');
    //             fetchCeremonies(); // Refresh the list
    //         } else {
    //             showSnackbar(`Failed to delete ceremony.`, 'error');
    //         }
    //     } catch (err) {
    //         console.error("Error deleting ceremony:", err);
    //     } finally {
    //         setDeleteConfirmOpen(false);
    //         setSelectedCeremony(null);
    //     }
    // };

    const handleConfirmDelete = async () => {
        if (!selectedCeremony) return;
        try {
            await api.delete(`/ceremonies/${selectedCeremony.id}`);
            showSnackbar(`${selectedCeremony.name} archived.`, 'success');
            fetchData();
        } catch (err) {
            showSnackbar('Failed to archive ceremony.', 'error');
        } finally {
            setDeleteConfirmOpen(false);
            setSelectedCeremony(null);
        }
    };

    // const handleReactivateClick = async (ceremony) => {
    //     try {
    //         const token = localStorage.getItem('token');
    //         const res = await fetch(`${process.env.REACT_APP_API_URL}/api/ceremonies/${ceremony.id}`, {
    //             method: 'PUT',
    //             headers: {
    //                 'Content-Type': 'application/json',
    //                 'Authorization': `Bearer ${token}`
    //             },
    //             body: JSON.stringify({
    //                 ...ceremony,
    //                 // Ensure dates are formatted correctly for the DB
    //                 ceremony_date: formatForInput(ceremony.ceremony_date),
    //                 open_date: formatForInput(ceremony.open_date),
    //                 cutoff_date: formatForInput(ceremony.cutoff_date),
    //                 is_active: true
    //             })
    //         });

    //         if (res.ok) {
    //             showSnackbar(`${ceremony.name} reactivated!`, 'success');
    //             fetchCeremonies();
    //         } else {
    //             showSnackbar('Failed to reactivate.', 'error');
    //         }
    //     } catch (err) {
    //         console.error("Error reactivating:", err);
    //     }
    // };

    const handleReactivateClick = async (ceremony) => {
        try {
            await api.put(`/ceremonies/${ceremony.id}`, {
                ...ceremony,
                ceremony_date: formatForInput(ceremony.ceremony_date),
                open_date: formatForInput(ceremony.open_date),
                cutoff_date: formatForInput(ceremony.cutoff_date),
                is_active: true
            });
            showSnackbar(`${ceremony.name} reactivated!`, 'success');
            fetchData();
        } catch (err) {
            showSnackbar('Failed to reactivate.', 'error');
        }
    };

    const validateForm = () => {
        const { name, ceremony_date, open_date, cutoff_date } = form;

        // 1. Strict Check: Are all fields filled?
        // Use .trim() on name to ensure it's not just whitespace
        const isFilled = name?.trim() && ceremony_date && open_date && cutoff_date;

        if (!isFilled) return false;

        // 2. Logic Check: Are dates in the correct order?
        const cDate = new Date(ceremony_date).setHours(0, 0, 0, 0);
        const oDate = new Date(open_date).setHours(0, 0, 0, 0);
        const kDate = new Date(cutoff_date).setHours(0, 0, 0, 0);

        const isSequenceValid = oDate < kDate && kDate < cDate;

        return isSequenceValid;
    };


    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>Ceremony Management</Typography>

            {canEdit && (
                <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={handleCreateClick} sx={{ mb: 3 }}>
                    Create Ceremony
                </Button>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>Ceremony Date</strong></TableCell>
                            <TableCell><strong>Submissions Open</strong></TableCell>
                            <TableCell><strong>Cutoff Date</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            {canEdit && <TableCell align="right"><strong>Actions</strong></TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {ceremonies.map((c) => {
                            const isDeleted = c.is_active === false;

                            return (
                                <TableRow key={c.id} hover>
                                    <TableCell
                                        sx={{
                                            cursor: c.is_active ? 'pointer' : 'default',
                                            color: c.is_active ? 'primary.main' : 'inherit',
                                            fontWeight: 500,
                                            '&:hover': {
                                                textDecoration: c.is_active ? 'underline' : 'none'
                                            },
                                            opacity: c.is_active ? 1 : 0.6
                                        }}
                                        onClick={() => {
                                            if (c.is_active) {
                                                navigate(`/ceremonies/${c.id}`);
                                            }
                                        }}
                                    >
                                        {c.name} {isDeleted && <strong>(Archived)</strong>}
                                    </TableCell>
                                    <TableCell sx={{ opacity: c.is_active ? 1 : 0.6 }}>{formatDate(c.ceremony_date)}</TableCell>
                                    <TableCell sx={{ opacity: c.is_active ? 1 : 0.6 }}>{formatDate(c.open_date)}</TableCell>
                                    <TableCell sx={{ opacity: c.is_active ? 1 : 0.6 }}>{formatDate(c.cutoff_date)}</TableCell>
                                    <TableCell sx={{ opacity: c.is_active ? 1 : 0.6 }}>
                                        {isDeleted ? (
                                            <Chip label="Deleted" variant="outlined" size="small" />
                                        ) : (
                                            isLocked(c.cutoff_date) ?
                                                <Chip label="Locked" color="error" size="small" /> :
                                                <Chip label="Open" color="success" size="small" />
                                        )}
                                    </TableCell>
                                    {canEdit && (
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                {/* Only show Edit if NOT deleted */}
                                                {!isDeleted && (
                                                    <IconButton color="primary" onClick={() => handleEditClick(c)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                )}

                                                {/* Toggle between Delete and Reactivate */}
                                                {isDeleted ? (
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="success"
                                                        onClick={() => handleReactivateClick(c)}
                                                    >
                                                        Reactivate
                                                    </Button>
                                                ) : (
                                                    <IconButton color="error" onClick={() => handleDeleteClick(c)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* CREATE MODAL */}
            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth={true}   // This tells the modal to take up the available width
                maxWidth="md"      // Options: 'xs', 'sm', 'md', 'lg', 'xl'
                disableRestoreFocus
            >
                <DialogTitle>
                    {isEditing ? `Edit Ceremony: ${form.name}` : 'Create New Ceremony'}
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
                        <TextField
                            label="Ceremony Name"
                            fullWidth
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            onBlur={() => {
                                if (!form.name) showSnackbar("Please enter a ceremony name", "info");
                            }}
                        />

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label="Ceremony Date"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={form.ceremony_date}
                                onChange={(e) => setForm({ ...form, ceremony_date: e.target.value })}
                                onBlur={(e) => handleCeremonyDateChange(e)}
                            />
                            <TextField
                                label="Open Date"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={form.open_date}
                                onChange={(e) => setForm({ ...form, open_date: e.target.value })}
                                helperText={`Set to ${delays.open} weeks before ceremony by default`}
                            />
                            <TextField
                                label="Cutoff Date"
                                type="date"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={form.cutoff_date}
                                onChange={(e) => setForm({ ...form, cutoff_date: e.target.value })}
                                helperText={`Set to ${delays.cutoff} weeks before ceremony by default`}
                            />
                        </Box>

                        {/* Show this only if they've started but it's still invalid */}
                        {!validateForm() && (form.name || form.ceremony_date) && (
                            <Typography color="text.secondary" variant="caption" sx={{ mt: 1, display: 'block' }}>
                                * All fields required. Dates must follow: Open → Cutoff → Ceremony.
                            </Typography>
                        )}

                        <Box sx={{ mt: 3, p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1">
                                    Linked Awards: <strong>{form.award_ids.length}</strong> selected
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setAwardsModalOpen(true)}
                                >
                                    {form.award_ids.length > 0 ? 'Edit Selection' : 'Add Awards'}
                                </Button>
                            </Box>
                            {form.award_ids.length > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {availableAwards
                                        .filter(a => form.award_ids.includes(a.id))
                                        .map(a => a.aw_1 || 'Untitled Award') // Adjust based on your JSONB structure
                                        .join(', ')}
                                </Typography>
                            )}
                        </Box>

                    </Box>
                </DialogContent>

                {/* RECALCULATION CONFIRMATION MODAL */}
                <Dialog open={confirmRecalcOpen} onClose={() => setConfirmRecalcOpen(false)}>
                    <DialogTitle>Update existing dates?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            You have already entered values for the Open or Cutoff dates.
                            Would you like to recalculate them based on the new ceremony date, or keep your manual entries?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            // Keep current values, only update the ceremony date
                            setForm({ ...form, ceremony_date: pendingDate });
                            setConfirmRecalcOpen(false);
                        }}>
                            Keep Current Values
                        </Button>
                        <Button variant="contained" color="primary" onClick={() => {
                            // Recalculate everything
                            performCalculation(pendingDate);
                            setConfirmRecalcOpen(false);
                        }}>
                            Recalculate Both
                        </Button>
                    </DialogActions>
                </Dialog>

                <DialogActions sx={{ p: 3, justifyContent: 'space-between' }}>
                    {/* Left side button */}
                    <Button
                        onClick={resetForm}
                        color="primary"
                        variant="outlined"
                        sx={{ mr: 'auto' }}
                    >
                        Clear Form
                    </Button>

                    {/* Right side buttons */}
                    <Box>
                        <Button onClick={() => setOpen(false)} sx={{ mr: 1 }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSave}
                            variant="contained"
                            disabled={!validateForm()}
                        >
                            {isEditing ? 'Update Ceremony' : 'Save Ceremony'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* DELETE CONFIRMATION */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                PaperProps={{ sx: { borderRadius: 3 } }} // Optional: matches your theme's rounded look
            >
                <DialogTitle sx={{ fontWeight: 'bold' }}>Delete Ceremony?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the <strong>{selectedCeremony?.name}</strong> ceremony?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        sx={{ px: 3 }}
                    >
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>


            {/* AWARDS SELECTION MODAL */}
            <Dialog open={awardsModalOpen} onClose={() => setAwardsModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Select Awards for [{form.name}] Ceremony</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.award_ids.length === availableAwards.length && availableAwards.length > 0}
                                    indeterminate={form.award_ids.length > 0 && form.award_ids.length < availableAwards.length}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setForm({ ...form, award_ids: availableAwards.map(a => a.id) });
                                        } else {
                                            setForm({ ...form, award_ids: [] });
                                        }
                                    }}
                                />
                            }
                            label={<strong>Select All Awards</strong>}
                        />
                    </Box>
                    <List dense sx={{ py: 0 }}>
                        {availableAwards.map((award) => (
                            <ListItem
                                key={award.id}
                                disablePadding
                                divider
                                sx={{ minHeight: 40 }}
                            >
                                <ListItemButton
                                    onClick={() => {
                                        const newIds = form.award_ids.includes(award.id)
                                            ? form.award_ids.filter(id => id !== award.id)
                                            : [...form.award_ids, award.id];
                                        setForm({ ...form, award_ids: newIds });
                                    }}
                                    sx={{ py: 0.5 }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        <Checkbox
                                            edge="start"
                                            size="small"
                                            checked={form.award_ids.includes(award.id)}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                    </ListItemIcon>

                                    {/* Custom layout for Side-by-Side text */}
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%'
                                    }}>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {award.aw_1 || `Award #${award.id}`}
                                        </Typography>

                                        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 2 }}>
                                            {award.aw_3 || ''}
                                        </Typography>
                                    </Box>
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAwardsModalOpen(false)} variant="contained">
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}